import { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Send, CheckCircle2, XCircle, Loader2, RotateCcw, Zap, StopCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/contexts/AuthContext";

const BATCH_SIZE = 25;

export function SendPanel({ smtpConfig, subject, body, rows, emailColumn, attachments }) {
  const [sending, setSending] = useState(false);
  const [logs, setLogs] = useState([]);
  const [progress, setProgress] = useState(0);
  const [summary, setSummary] = useState(null);
  const [currentBatch, setCurrentBatch] = useState(0);
  const [totalBatches, setTotalBatches] = useState(0);
  const logsEndRef = useRef(null);
  const cancelRef = useRef(false);

  useEffect(() => {
    logsEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [logs]);

  const addLog = useCallback((type, text) => {
    setLogs((prev) => [...prev, { type, text, time: new Date() }]);
  }, []);

  const handleSend = async () => {
    if (!smtpConfig.host || !smtpConfig.email || !smtpConfig.password) {
      toast.error("Configurez le SMTP d'abord");
      return;
    }
    if (!subject.trim()) {
      toast.error("L'objet de l'email est requis");
      return;
    }
    if (!body.trim()) {
      toast.error("Le corps de l'email est requis");
      return;
    }
    if (!rows || rows.length === 0) {
      toast.error("Aucun destinataire");
      return;
    }

    setSending(true);
    setLogs([]);
    setSummary(null);
    setProgress(0);
    cancelRef.current = false;

    const totalRecipients = rows.length;
    const batches = [];
    for (let i = 0; i < totalRecipients; i += BATCH_SIZE) {
      batches.push(rows.slice(i, i + BATCH_SIZE));
    }
    setTotalBatches(batches.length);

    const attPayload = attachments && attachments.length > 0
      ? attachments.map((a) => ({ name: a.name, data: a.data, content_type: a.content_type }))
      : null;

    addLog("info", `Démarrage de l'envoi : ${totalRecipients} destinataires en ${batches.length} lots de ${BATCH_SIZE}`);

    let totalSuccess = 0;
    let totalFailed = 0;
    let totalSent = 0;

    for (let batchIdx = 0; batchIdx < batches.length; batchIdx++) {
      if (cancelRef.current) {
        addLog("info", `Envoi annulé par l'utilisateur après ${totalSent} emails`);
        break;
      }

      const batch = batches[batchIdx];
      setCurrentBatch(batchIdx + 1);
      addLog("info", `Lot ${batchIdx + 1}/${batches.length} — ${batch.length} emails — Connexion SMTP...`);

      try {
        const res = await api.post("/send-emails", {
          smtp: smtpConfig,
          subject,
          body,
          recipients: batch,
          email_column: emailColumn,
          attachments: attPayload,
        }, { timeout: 120000 });

        if (!res.data.success) {
          addLog("error", `Lot ${batchIdx + 1} — Erreur SMTP : ${res.data.message}`);
          // Count all as failed for this batch
          totalFailed += batch.length;
          totalSent += batch.length;
          setProgress(Math.round((totalSent / totalRecipients) * 100));
          continue;
        }

        // Process results from this batch
        for (const r of res.data.results) {
          totalSent++;
          if (r.status === "ok") {
            totalSuccess++;
            addLog("success", `[OK] ${r.email}`);
          } else {
            totalFailed++;
            addLog("error", `[ERREUR] ${r.email} — ${r.error}`);
          }
          setProgress(Math.round((totalSent / totalRecipients) * 100));
        }

        addLog("info", `Lot ${batchIdx + 1} terminé — ${res.data.summary.success} OK / ${res.data.summary.failed} erreurs`);

      } catch (err) {
        const msg = err.response?.data?.detail || err.message || "Erreur réseau";
        addLog("error", `Lot ${batchIdx + 1} — Erreur : ${msg}`);
        totalFailed += batch.length;
        totalSent += batch.length;
        setProgress(Math.round((totalSent / totalRecipients) * 100));
      }

      // Small delay between batches to avoid rate limiting
      if (batchIdx < batches.length - 1 && !cancelRef.current) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
    }

    // Final summary
    const finalSummary = { total: totalSent, success: totalSuccess, failed: totalFailed };
    setSummary(finalSummary);

    if (cancelRef.current) {
      addLog("info", `Résumé partiel : ${totalSuccess} réussis, ${totalFailed} échoués sur ${totalSent} traités`);
      toast.warning(`Envoi annulé — ${totalSuccess} réussis sur ${totalSent}`);
    } else if (totalFailed === 0) {
      addLog("info", `Terminé — ${totalSuccess} emails envoyés avec succès`);
      toast.success(`${totalSuccess} emails envoyés avec succès`);
    } else {
      addLog("info", `Terminé — ${totalSuccess} réussis, ${totalFailed} échoués`);
      toast.warning(`${totalSuccess} réussis, ${totalFailed} échoués`);
    }

    setSending(false);
    setCurrentBatch(0);
  };

  const handleCancel = () => {
    cancelRef.current = true;
    toast.info("Annulation en cours... Le lot actuel sera terminé.");
  };

  const handleReset = () => {
    setLogs([]);
    setSummary(null);
    setProgress(0);
    setCurrentBatch(0);
    setTotalBatches(0);
  };

  const canSend = smtpConfig.host && smtpConfig.email && smtpConfig.password && subject && body && rows?.length > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.3 }}
      className="space-y-5"
      data-testid="send-panel-section"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Zap className="w-4 h-4 text-primary" />
          <h3 className="text-sm font-bold tracking-wide uppercase text-zinc-500">
            Envoi
          </h3>
        </div>
        <div className="flex items-center gap-2">
          {rows && (
            <Badge variant="outline" className="text-xs font-mono" data-testid="recipient-count-badge">
              {rows.length} destinataires
            </Badge>
          )}
          {rows && rows.length > BATCH_SIZE && (
            <Badge variant="outline" className="text-xs font-mono" data-testid="batch-count-badge">
              {Math.ceil(rows.length / BATCH_SIZE)} lots
            </Badge>
          )}
          {attachments && attachments.length > 0 && (
            <Badge variant="outline" className="text-xs font-mono" data-testid="attachment-send-count-badge">
              {attachments.length} pièce{attachments.length > 1 ? "s" : ""} jointe{attachments.length > 1 ? "s" : ""}
            </Badge>
          )}
        </div>
      </div>

      {/* Send / Cancel / Reset buttons */}
      <div className="flex gap-3">
        {!sending ? (
          <Button
            className="flex-1 gap-2 rounded-full h-12 text-base font-semibold hover:scale-[1.02] active:scale-[0.98] transition-transform"
            onClick={handleSend}
            disabled={!canSend}
            data-testid="send-emails-button"
          >
            <Send className="w-5 h-5" />
            Envoyer les emails
          </Button>
        ) : (
          <>
            <Button
              className="flex-1 gap-2 rounded-full h-12 text-base font-semibold"
              disabled
              data-testid="sending-in-progress-button"
            >
              <Loader2 className="w-5 h-5 animate-spin" />
              Lot {currentBatch}/{totalBatches} en cours...
            </Button>
            <Button
              variant="destructive"
              className="gap-2 rounded-full h-12 px-6"
              onClick={handleCancel}
              data-testid="cancel-send-button"
            >
              <StopCircle className="w-4 h-4" />
              Arrêter
            </Button>
          </>
        )}
        {(logs.length > 0 && !sending) && (
          <Button
            variant="outline"
            className="gap-2 rounded-full h-12"
            onClick={handleReset}
            data-testid="reset-logs-button"
          >
            <RotateCcw className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Progress bar */}
      <AnimatePresence>
        {(sending || progress > 0) && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            className="space-y-2"
          >
            <div className="flex items-center justify-between text-xs text-zinc-500">
              <span>
                {sending
                  ? `Lot ${currentBatch}/${totalBatches} en cours`
                  : "Terminé"
                }
              </span>
              <span className="font-mono font-medium" data-testid="progress-percentage">{progress}%</span>
            </div>
            <div className="h-2 bg-zinc-100 rounded-full overflow-hidden" data-testid="progress-bar">
              <motion.div
                className="h-full bg-primary rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.3, ease: "easeOut" }}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Terminal logs */}
      <AnimatePresence>
        {logs.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            className="terminal-logs bg-[#09090B] rounded-xl p-5 font-mono text-xs md:text-sm h-[320px] overflow-y-auto border border-zinc-800 shadow-2xl"
            data-testid="send-logs-terminal"
          >
            <div className="flex items-center gap-2 mb-4">
              <div className="w-3 h-3 rounded-full bg-red-500" />
              <div className="w-3 h-3 rounded-full bg-yellow-500" />
              <div className="w-3 h-3 rounded-full bg-green-500" />
              <span className="text-zinc-600 text-xs ml-2">send-logs</span>
            </div>
            {logs.map((log, i) => (
              <div
                key={i}
                className={`animate-log-slide flex gap-2 items-start py-0.5 ${
                  log.type === "success"
                    ? "text-emerald-400"
                    : log.type === "error"
                    ? "text-rose-400"
                    : "text-zinc-500"
                }`}
              >
                {log.type === "success" && <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />}
                {log.type === "error" && <XCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />}
                {log.type === "info" && <span className="text-blue-400">{'>'}</span>}
                <span>{log.text}</span>
              </div>
            ))}
            <div ref={logsEndRef} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Summary */}
      <AnimatePresence>
        {summary && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-3 gap-4"
            data-testid="send-summary"
          >
            <div className="p-4 rounded-xl border border-zinc-200 bg-white text-center">
              <p className="text-2xl font-black text-zinc-900" data-testid="summary-total">{summary.total}</p>
              <p className="text-xs text-zinc-500 mt-1 uppercase tracking-wide font-medium">Total</p>
            </div>
            <div className="p-4 rounded-xl border border-emerald-200 bg-emerald-50 text-center">
              <p className="text-2xl font-black text-emerald-600" data-testid="summary-success">{summary.success}</p>
              <p className="text-xs text-emerald-600 mt-1 uppercase tracking-wide font-medium">Réussis</p>
            </div>
            <div className="p-4 rounded-xl border border-red-200 bg-red-50 text-center">
              <p className="text-2xl font-black text-red-600" data-testid="summary-failed">{summary.failed}</p>
              <p className="text-xs text-red-600 mt-1 uppercase tracking-wide font-medium">Échoués</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
